import { Box, Divider, Flex, HStack, IconButton, Image, Text, VStack, useToast } from "@chakra-ui/react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";

import { useDisclosure, useIsMobile } from "@equidefi/hooks";
import { useAuth } from "@equidefi/portals/hooks/useAuth";
import { useCurrentUser } from "@equidefi/portals/hooks/useUsers";
import { Icon } from "@equidefi/ui/icon";

import { EnvironmentCard } from "@/components/EnvironmentCard";
import { CanView } from "@/components/permissions/Can";
import { CONFIG } from "@/constants/config";
import Logo from "@/images/equidefi-logo-color-black.svg";

export const NavBar = ({ children }) => {
  const toast = useToast();
  const menu = useDisclosure();
  const { handleLogout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile("lg");
  const { data: user } = useCurrentUser();
  const MenuIcon = menu.isOpen ? Icon.X : Icon.Menu;

  return (
    <>
      <Flex direction="column" zIndex="2" minH="full" gap={0} w="full" maxW={{ base: "full", lg: "190px" }}>
        <Flex direction="column" {...(!isMobile ? { pos: "fixed", maxW: "190px", h: "100vh" } : {})}>
          <HStack p="5" w="full">
            {isMobile && (
              <IconButton
                aria-label={menu.isOpen ? "Hide Menu" : "Open Menu"}
                icon={<MenuIcon size="1.5em" />}
                variant="unstyled"
                onClick={menu.onToggle}
              />
            )}
            <Box flexGrow="1">
              <Link to="/v2/offerings">
                <Image src={Logo} w="150px" mx="auto" />
              </Link>
            </Box>
            {isMobile && (
              <EnvironmentCard
                transform="scale(0.8)"
                w="initial"
                px="3"
                fallback={<Box bg="transparent" boxSize="40px" />}
              />
            )}
          </HStack>

          {!isMobile && <EnvironmentCard />}

          {(menu.isOpen || !isMobile) && (
            <>
              <VStack mb="auto" as="nav" mt={{ base: 0, lg: 100 }} gap="2.5" minW="full">
                <NavItem to="/offerings" Icon={Icon.Folder}>
                  Offerings
                </NavItem>
                <NavItem to="/issuers" Icon={Icon.Briefcase}>
                  Issuers
                </NavItem>
                <NavItem to="/brokers" Icon={Icon.TrendingUp}>
                  Brokers
                </NavItem>
                <CanView admin>
                  <NavItem to="/vault" Icon={Icon.Users}>
                    Investors
                  </NavItem>
                </CanView>
                <NavItem to="/account" Icon={Icon.User}>
                  Account
                </NavItem>
                <CanView admin>
                  <NavItem to={CONFIG.ADMIN_URL} Icon={Icon.Tool} target="_blank">
                    Admin
                  </NavItem>
                </CanView>
              </VStack>

              <VStack mt="auto" mb={4} minW="full">
                <VStack align="center" justify="space-between" w="full">
                  <Text fontSize="md" fontWeight="bold" color="black" my={0}>
                    {user?.full_name}
                  </Text>
                  <Flex
                    mt={-2}
                    align="center"
                    cursor="pointer"
                    color="gray.500"
                    onClick={() => {
                      handleLogout();
                      navigate("/");
                      toast({
                        status: "success",
                        description: "Logged out",
                      });
                    }}>
                    <Text my={0} mr={4} fontSize="md">
                      Log Out
                    </Text>
                    <Icon.LogOut size="1.2em" />
                  </Flex>
                </VStack>
              </VStack>
            </>
          )}
          {isMobile && <Divider m="0" />}
        </Flex>
      </Flex>
      <Box w="full" overflow="auto">
        {children}
      </Box>
    </>
  );
};

const NavItem = ({ onClick = () => {}, to, children, Icon, ...props }) => {
  const Action = to ? NavLink : Box;
  const { pathname } = useLocation();
  const isCurrentPage = to && pathname === to;

  return (
    <Box
      as={Action}
      onClick={onClick}
      to={to}
      cursor="pointer"
      w="full"
      color={isCurrentPage && "purple.500"}
      fontWeight={isCurrentPage && "700"}
      {...props}>
      <Flex gap={5} opacity="90%">
        <Box w="2" bgColor={isCurrentPage && "purple.500"} borderRightRadius="5px" />
        <Flex my={2} gap={2.5} lineHeight={"24px"}>
          <Box as={Icon} opacity="90%" size="1.6em" />
          {children}
        </Flex>
      </Flex>
    </Box>
  );
};
